import restClient from "api/restClient";

/**
 * A single ranked result from the backend full-text search endpoint
 * (`GET /fulltext-search`). The backend already applies RBAC read-path
 * filtering and excludes trashed records, so results are safe to render as-is.
 */
export interface FullTextSearchResult {
    id: string;
    record_type: string;
    name: string | null;
    // Resolved display glyph (own icon, else the record-type default).
    resolved_icon?: string | null;
    // A custom object's object_template id (null for other record types);
    // the row resolves the template icon from it.
    resolved_object_template_id?: string | null;
    // Denormalized parent-record comment count (0/absent when none);
    // powers the comment indicator badge on full-text-search rows.
    comment_count?: number | null;
}

interface FullTextSearchResponse {
    query: string;
    results: FullTextSearchResult[];
}

interface FetchFullTextSearchOptions {
    /** Restrict to these record types (e.g. ["FLOW", "CUSTOM_OBJECT"]). Empty/omitted = all indexed types. */
    types?: string[];
    /** Maximum number of results (backend hard-caps this). */
    limit?: number;
    /** AbortController signal so a superseded/blurred request can be cancelled. */
    signal?: AbortSignal;
}

/**
 * Call the backend full-text search endpoint and return the ranked results.
 *
 * Mirrors the quick name-search request lifecycle in `SearchBar`: the caller is
 * expected to guard against stale responses (request-id / abort). The last token
 * of `term` is prefix-matched server-side, so no client-side wildcarding is needed.
 */
export const fetchFullTextSearch = async (
    term: string,
    options: FetchFullTextSearchOptions = {}
): Promise<FullTextSearchResult[]> => {
    const { types, limit = 10, signal } = options;
    const response = await restClient.get("/fulltext-search", {
        params: {
            q: term,
            // Backend expects a comma-separated string; omit entirely to search all types.
            ...(types && types.length ? { types: types.join(",") } : {}),
            limit,
        },
        signal,
    });
    const data = response?.data as FullTextSearchResponse | undefined;
    return data?.results ?? [];
};
